/**
 * NotificationBell Component
 * Bell icon with unread badge and dropdown for latest notifications.
 */

import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  CheckCheck,
  Users,
  CreditCard,
  Mail,
  Calendar,
  AlertCircle,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/formatters';

// ─── Types ────────────────────────────────────────────────────────
type NotificationType = 'member' | 'donation' | 'contact' | 'event' | 'system';

interface Notification {
  id: number;
  type: NotificationType;
  title: string;
  message?: string;
  createdAt: string;
  read: boolean;
}

interface NotificationBellProps {
  unreadCount: number;
  notifications: Notification[];
  onMarkRead: (id: number) => void;
  onMarkAllRead: () => void;
  onNotificationClick: (notification: Notification) => void;
  isOpen: boolean;
  onToggle: () => void;
}

// ─── Notification type config ─────────────────────────────────────
const NOTIFICATION_ICONS: Record<NotificationType, typeof Bell> = {
  member: Users,
  donation: CreditCard,
  contact: Mail,
  event: Calendar,
  system: AlertCircle,
};

const NOTIFICATION_COLORS: Record<NotificationType, string> = {
  member: 'bg-blue-100 text-blue-600',
  donation: 'bg-green-100 text-green-600',
  contact: 'bg-purple-100 text-purple-600',
  event: 'bg-amber-100 text-amber-600',
  system: 'bg-red-100 text-red-600',
};

// ─── Component ────────────────────────────────────────────────────
export function NotificationBell({
  unreadCount,
  notifications,
  onMarkRead,
  onMarkAllRead,
  onNotificationClick,
  isOpen,
  onToggle,
}: NotificationBellProps) {
  const { t, i18n } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        if (isOpen) onToggle();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  const latestNotifications = notifications.slice(0, 5);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={onToggle}
        className={cn(
          'relative rounded-lg p-2 transition-colors',
          'hover:bg-slate-100 dark:hover:bg-slate-700',
          isOpen && 'bg-slate-100 dark:bg-slate-700',
        )}
        aria-label={t('notifications.bell', 'Notifications')}
      >
        <Bell className="h-5 w-5 text-slate-600 dark:text-slate-300" />

        {/* Unread badge */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -right-0.5 -top-0.5 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white dark:ring-slate-800"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-700">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {t('notifications.title', 'Notifications')}
              </h4>
              {unreadCount > 0 && (
                <button
                  onClick={onMarkAllRead}
                  className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  {t('notifications.markAllRead', 'Mark all read')}
                </button>
              )}
            </div>

            {/* Notification List */}
            <div className="max-h-80 overflow-y-auto">
              {latestNotifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Bell className="mx-auto mb-2 h-8 w-8 text-slate-300 dark:text-slate-600" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('notifications.empty', 'No notifications yet')}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                  {latestNotifications.map((notification) => {
                    const Icon = NOTIFICATION_ICONS[notification.type] ?? Bell;
                    const colorClass = NOTIFICATION_COLORS[notification.type] ?? 'bg-slate-100 text-slate-600';

                    return (
                      <li key={notification.id}>
                        <button
                          onClick={() => {
                            if (!notification.read) onMarkRead(notification.id);
                            onNotificationClick(notification);
                          }}
                          className={cn(
                            'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-750',
                            !notification.read && 'bg-blue-50/50 dark:bg-blue-900/10',
                          )}
                        >
                          {/* Type Icon */}
                          <div
                            className={cn(
                              'mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
                              colorClass,
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </div>

                          {/* Content */}
                          <div className="min-w-0 flex-1">
                            <p
                              className={cn(
                                'truncate text-sm',
                                notification.read
                                  ? 'text-slate-600 dark:text-slate-400'
                                  : 'font-medium text-slate-900 dark:text-slate-100',
                              )}
                            >
                              {notification.title}
                            </p>
                            <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
                              {formatRelativeTime(notification.createdAt, i18n.language)}
                            </p>
                          </div>

                          {/* Unread dot */}
                          {!notification.read && (
                            <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Footer */}
            {latestNotifications.length > 0 && (
              <div className="border-t border-slate-100 dark:border-slate-700">
                <Link
                  to="/admin/notifications"
                  onClick={onToggle}
                  className="flex items-center justify-center gap-1 px-4 py-2.5 text-xs font-medium text-blue-600 transition-colors hover:bg-slate-50 dark:text-blue-400 dark:hover:bg-slate-750"
                >
                  {t('notifications.viewAll', 'View all notifications')}
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default NotificationBell;
